import React, { useState, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import {
  institutionLoginsActions,
  institutionLoginsSelectors,
  institutionLoginsTypes,
} from 'app-components/flux/institution-logins';
import type { RefreshFrequency } from 'app-components/flux/institution-logins/institutionLoginsTypes';

import Box from '@mui/material/Box';
import type { DialogProps } from '@mui/material/Dialog';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import InstitutionItem from 'components/InstitutionItem';
import { useAppSelector } from 'hooks/redux';

import QChoiceChips from 'components/QuickenControls/QChoiceChips';

const useStyles = makeStyles()((theme) => ({
  paper: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

type UpdateOption = {
  refreshFrequency: RefreshFrequency;
  label: string;
};
const updateOptions: UpdateOption[] = [
  {
    refreshFrequency: 'NONE',
    label: 'Always (default)',
  },
  {
    refreshFrequency: 'DAILY',
    label: 'Daily',
  },
  {
    refreshFrequency: 'WEEKLY',
    label: 'Weekly',
  },
  {
    refreshFrequency: 'MONTHLY',
    label: 'Monthly',
  },
  {
    refreshFrequency: 'NEVER',
    label: 'Never (manual refresh only)',
  },
];

export type ManageUpdatesDialogProps = Omit<DialogProps, 'open'> &
  Partial<Pick<DialogProps, 'open'>> & {
    defaultValue: string; // institution login id
  };

const ManageUpdatesDialog = ({ defaultValue, ...props }: ManageUpdatesDialogProps) => {
  const dispatch = useDispatch();
  const stdDialogRef = useRef<any>(undefined);
  const { classes } = useStyles();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const institutionLogin = useAppSelector((state) =>
    institutionLoginsSelectors.getInstitutionLoginForId(state, defaultValue),
  );
  const [selectedRefreshFrequency, setSelectedRefreshFrequency] = useState<RefreshFrequency>(
    institutionLogin?.refreshFrequency || 'NONE',
  );
  const defaultSelectedItems = useMemo(() => {
    const updateOption = updateOptions.find(
      (updateOption) => updateOption.refreshFrequency === institutionLogin?.refreshFrequency,
    );
    return updateOption ? [updateOption] : [];
  }, [institutionLogin?.refreshFrequency]);

  return (
    <StdDialog showCloseButton size='md' title='Manage updates' stdDialogRef={stdDialogRef} {...props}>
      <StdDialogContent id='manage_updates_dialog_content'>
        <Typography variant='bodyMedium'>Why would I change my account update intervals?</Typography>
        <Typography variant='paragraphRegular' color='text.secondary'>
          Simplifi updates your accounts when you click update or log in, provided a few hours have passed since the
          last update. If you don’t need updates at each login or your bank requires Multi-factor authentication (MFA),
          you can set a longer refresh interval below.
        </Typography>
        <Typography variant='paragraphRegular' color='text.secondary'>
          <b>Note:</b> For financial institutions requiring MFA at each connection, you can adjust the security setting
          in your bank connection settings.
        </Typography>
        <br />

        <Paper className={classes.paper}>
          <InstitutionItem value={institutionLogin?.id} />

          <Box display='flex' gap={2} alignItems='center'>
            <Typography variant='captionMedium'>Set max update interval:</Typography>
            <QChoiceChips
              items={updateOptions}
              size='small'
              defaultSelectedItems={defaultSelectedItems}
              keyFromItem={(item) => item.refreshFrequency}
              labelFromItem={(item) => item.label}
              onSelected={([item]) => {
                if (item && institutionLogin) {
                  setSelectedRefreshFrequency(item.refreshFrequency);
                }
              }}
            />
          </Box>
        </Paper>
      </StdDialogContent>
      <StdDialogActions
        primary={{
          disabled: institutionLogin?.refreshFrequency === selectedRefreshFrequency,
          children: 'Save Updates',
          loading,
          tooltipText: error,
          showTooltip: Boolean(error),
          onClick: () => {
            if (institutionLogin && selectedRefreshFrequency) {
              setLoading(true);
              new Promise((resolve, reject) =>
                dispatch(
                  institutionLoginsActions.updateInstitutionLogin(
                    institutionLoginsTypes.mkInstitutionLoginPatch({
                      id: institutionLogin.id,
                      refreshFrequency: selectedRefreshFrequency,
                    }) as institutionLoginsTypes.InstitutionLogin,
                    { resolve, reject, undo: { userMessage: 'Schedule updated' } },
                  ),
                ),
              )
                .then(() => {
                  stdDialogRef.current?.closeDialog();
                  setLoading(false);
                  setError('');
                })
                .catch((e) => {
                  setLoading(false);
                  setError('Sorry, but we were unable to make your changes. Please try again.');
                  assert(false, e);
                });
            }
          },
        }}
      />
    </StdDialog>
  );
};
export default ManageUpdatesDialog;
